import { useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { PostHogInstance } from 'shared/posthogCapture/types';
import { EmailProvider } from 'shared/components/EmailComposeMenu/_index';
import { ReflexFeedbackRating } from 'shiftNotes/_types';

interface CaptureInputTypeProperties {
  input_type: 'microphone' | 'text';
}

interface CaptureReceivedErrorProperties {
  input_type: 'microphone' | 'text';
  error_type: string;
}

interface CaptureModeToggledProperties {
  from: 'voice' | 'text';
  to: 'voice' | 'text';
}

interface CaptureAttachmentProperties {
  media_type: 'image' | 'video';
  from_state: 'text' | 'recording' | 'paused';
}

interface CaptureRecordingPausedProperties {
  reason: 'attachment' | 'manual';
}

interface CaptureResponseExpandedProperties {
  note_count: number;
}

interface CaptureResponseClosedProperties {
  note_count: number;
  was_expanded: boolean;
}

interface NoteIdProperties {
  shift_note_id: string;
}

interface NoteUnsyncedFromEventProperties {
  shift_note_id: string;
  event_id?: string;
  user_id?: string;
}

interface NoteAttachmentsScrolledProperties {
  shift_note_id: string;
  direction: 'previous' | 'next';
}

interface NoteAttachmentResizedProperties {
  shift_note_id: string;
  input_method: 'pointer' | 'keyboard';
}

interface NoteReflexPhotoAttachedProperties {
  shift_note_id: string;
  reflex_id: string;
  step_id: string;
}

interface NoteReflexMessageProperties {
  shift_note_id: string;
  reflex_id: string;
  step_id: string;
}

interface NoteReflexMissingInfoProperties {
  shift_note_id: string;
  reflex_id: string;
  step_id: string;
}

interface NoteReflexGiveAnswerSourceProperties {
  shift_note_id: string;
  reflex_id: string;
  step_id: string;
  source_type: 'internal' | 'external';
}

interface NoteReflexPrefillFormOpenedProperties {
  shift_note_id: string;
  reflex_id: string;
  step_id: string;
  is_filled: boolean;
}

interface NoteFormCardOpenedProperties {
  shift_note_id: string;
  form_instance_id: string;
}

interface EmailProviderProperties {
  provider: EmailProvider;
}

interface NoteReflexMessageSentProperties extends NoteReflexMessageProperties {
  provider: EmailProvider;
}

interface ProfileDropdownOpenedProperties {
  has_profile: boolean;
}

interface NoteReflexFeedbackRatedProperties {
  shift_note_id: string;
  reflex_id: string;
  step_id?: string;
  question_id?: string;
  rating: ReflexFeedbackRating;
  version: number;
}

interface NoteReflexFeedbackSubmittedProperties {
  shift_note_id: string;
  reflex_id: string;
  version: number;
  section: 'reflex' | 'missing_info';
  negative_count: number;
  has_overall_comment: boolean;
  regenerated: boolean;
}

interface NoteReflexFeedbackSubmitErrorProperties {
  shift_note_id: string;
  reflex_id: string;
  version: number;
  section: 'reflex' | 'missing_info';
}

interface NoteReflexFeedbackBulkApprovedProperties {
  shift_note_id: string;
  reflex_id: string;
  version: number;
  step_count: number;
}

interface NoteReflexVersionSelectedProperties {
  shift_note_id: string;
  reflex_id: string;
  version: number;
}

interface ProfileCreateNewClickedProperties {
  has_query: boolean;
}

export const createShiftNotesCaptureEvents = (posthog: PostHogInstance) => ({
  captureCreatedNote: (properties: CaptureInputTypeProperties) => {
    posthog?.capture('shift notes / capture / created note', properties);
  },
  captureReceivedError: (properties: CaptureReceivedErrorProperties) => {
    posthog?.capture('shift notes / capture / received error', properties);
  },
  captureErrorDismissed: (properties: CaptureInputTypeProperties) => {
    posthog?.capture('shift notes / capture / error / dismissed', properties);
  },
  captureErrorRetried: (properties: CaptureInputTypeProperties) => {
    posthog?.capture('shift notes / capture / error / retried', properties);
  },
  captureModeToggled: (properties: CaptureModeToggledProperties) => {
    posthog?.capture('shift notes / capture / mode toggled', properties);
  },
  captureAttachmentAdded: (properties: CaptureAttachmentProperties) => {
    posthog?.capture('shift notes / capture / attachment / added', properties);
  },
  captureAttachmentRemoved: (properties: CaptureAttachmentProperties) => {
    posthog?.capture(
      'shift notes / capture / attachment / removed',
      properties
    );
  },
  captureRecordingPaused: (properties: CaptureRecordingPausedProperties) => {
    posthog?.capture('shift notes / capture / recording / paused', properties);
  },
  captureRecordingResumed: () => {
    posthog?.capture('shift notes / capture / recording / resumed');
  },
  captureInlineWritingOpened: () => {
    posthog?.capture('shift notes / capture / inline / writing / opened');
  },
  captureInlineResolveOpened: () => {
    posthog?.capture('shift notes / capture / inline / resolve / opened');
  },
  captureNoAudioWarningShown: () => {
    posthog?.capture('shift notes / capture / no audio warning shown');
  },
  captureResponseExpanded: (properties: CaptureResponseExpandedProperties) => {
    posthog?.capture('shift notes / capture / response / expanded', properties);
  },
  captureResponseClosed: (properties: CaptureResponseClosedProperties) => {
    posthog?.capture('shift notes / capture / response / closed', properties);
  },
  noteCopied: () => {
    posthog?.capture('shift notes / note / copied');
  },
  noteDeleted: () => {
    posthog?.capture('shift notes / note / deleted');
  },
  noteUnsyncedFromEvent: (properties: NoteUnsyncedFromEventProperties) => {
    posthog?.capture('shift notes / note / unsynced from event', properties);
  },
  noteMarkedPrivate: () => {
    posthog?.capture('shift notes / note / privacy / marked private');
  },
  noteMarkedPublic: () => {
    posthog?.capture('shift notes / note / privacy / marked public');
  },
  noteMarkedFalseAlarm: () => {
    posthog?.capture('shift notes / note / marked false alarm');
  },
  noteMarkPrivateEmergencyWarningShown: () => {
    posthog?.capture(
      'shift notes / note / privacy / emergency warning / shown'
    );
  },
  noteMarkPrivateEmergencyWarningConfirmed: () => {
    posthog?.capture(
      'shift notes / note / privacy / emergency warning / confirmed'
    );
  },
  noteMarkPrivateEmergencyWarningCancelled: () => {
    posthog?.capture(
      'shift notes / note / privacy / emergency warning / cancelled'
    );
  },
  noteEditModeOpened: () => {
    posthog?.capture('shift notes / note / edit mode / opened');
  },
  noteEditModeSubmitted: () => {
    posthog?.capture('shift notes / note / edit mode / submitted');
  },
  noteEditModeCancelled: (properties: NoteIdProperties) => {
    posthog?.capture('shift notes / note / edit mode / cancelled', properties);
  },
  noteEditModeTaggedProfile: () => {
    posthog?.capture('shift notes / note / edit mode / tagged profile');
  },
  notePhotoAttached: (properties: NoteIdProperties) => {
    posthog?.capture('shift notes / note / photo / attached', properties);
  },
  noteAttachmentDeleted: (properties: NoteIdProperties) => {
    posthog?.capture('shift notes / note / attachment / deleted', properties);
  },
  noteAttachmentOpened: (properties: NoteIdProperties) => {
    posthog?.capture('shift notes / note / attachment / opened', properties);
  },
  noteAttachmentResized: (properties: NoteAttachmentResizedProperties) => {
    posthog?.capture('shift notes / note / attachment / resized', properties);
  },
  noteAttachmentsScrolled: (properties: NoteAttachmentsScrolledProperties) => {
    posthog?.capture('shift notes / note / attachments / scrolled', properties);
  },
  noteProfileClickedLink: () => {
    posthog?.capture('shift notes / note / profile / clicked link');
  },
  noteProfileSelectedOption: () => {
    posthog?.capture('shift notes / note / profile / selected option');
  },
  noteProfileDropdownOpened: (properties: ProfileDropdownOpenedProperties) => {
    posthog?.capture(
      'shift notes / note / profile / dropdown opened',
      properties
    );
  },
  noteProfileCreateNewClicked: (
    properties: ProfileCreateNewClickedProperties
  ) => {
    posthog?.capture(
      'shift notes / note / profile / create new clicked',
      properties
    );
  },
  noteShared: (properties: EmailProviderProperties) => {
    posthog?.capture('shift notes / note / shared', properties);
  },
  noteReflexPhotoAttached: (properties: NoteReflexPhotoAttachedProperties) => {
    posthog?.capture(
      'shift notes / note / reflex / photo attached',
      properties
    );
  },
  noteReflexPhotoError: (properties: NoteReflexPhotoAttachedProperties) => {
    posthog?.capture('shift notes / note / reflex / photo / error', properties);
  },
  noteReflexMessageCopied: (properties: NoteReflexMessageProperties) => {
    posthog?.capture(
      'shift notes / note / reflex / message copied',
      properties
    );
  },
  noteReflexMessageSent: (properties: NoteReflexMessageSentProperties) => {
    posthog?.capture('shift notes / note / reflex / message sent', properties);
  },
  noteReflexMissingInfoAnswered: (
    properties: NoteReflexMissingInfoProperties
  ) => {
    posthog?.capture(
      'shift notes / note / reflex / missing info / answered',
      properties
    );
  },
  noteReflexMissingInfoAnswerError: (
    properties: NoteReflexMissingInfoProperties
  ) => {
    posthog?.capture(
      'shift notes / note / reflex / missing info / answer error',
      properties
    );
  },
  noteReflexGiveAnswerSourceClicked: (
    properties: NoteReflexGiveAnswerSourceProperties
  ) => {
    posthog?.capture(
      'shift notes / note / reflex / give answer / source clicked',
      properties
    );
  },
  noteReflexPrefillFormOpened: (
    properties: NoteReflexPrefillFormOpenedProperties
  ) => {
    posthog?.capture(
      'shift notes / note / reflex / prefill form / opened',
      properties
    );
  },
  noteReflexFeedbackRated: (properties: NoteReflexFeedbackRatedProperties) => {
    posthog?.capture(
      'shift notes / note / reflex / feedback / rated',
      properties
    );
  },
  noteReflexFeedbackSubmitted: (
    properties: NoteReflexFeedbackSubmittedProperties
  ) => {
    posthog?.capture(
      'shift notes / note / reflex / feedback / submitted',
      properties
    );
  },
  noteReflexFeedbackSubmitError: (
    properties: NoteReflexFeedbackSubmitErrorProperties
  ) => {
    posthog?.capture(
      'shift notes / note / reflex / feedback / submit error',
      properties
    );
  },
  noteReflexFeedbackBulkApproved: (
    properties: NoteReflexFeedbackBulkApprovedProperties
  ) => {
    posthog?.capture(
      'shift notes / note / reflex / feedback / bulk approved',
      properties
    );
  },
  noteReflexVersionSelected: (
    properties: NoteReflexVersionSelectedProperties
  ) => {
    posthog?.capture(
      'shift notes / note / reflex / version / selected',
      properties
    );
  },
  noteFormCardOpened: (properties: NoteFormCardOpenedProperties) => {
    posthog?.capture('shift notes / note / form card / opened', properties);
  },
});

export type ShiftNotesCaptureEvents = ReturnType<
  typeof createShiftNotesCaptureEvents
>;

export const usePosthogCapture = () => {
  const posthog = usePostHog();
  return useMemo(() => createShiftNotesCaptureEvents(posthog), [posthog]);
};
